import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { Trophy, Lock, User } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";


type Achievement = {
  id: string;
  name: string;
  description: string;
  icon: string;
  points_reward: number;
  requirement_type: string;
  requirement_value: number;
};

type UserAchievement = {
  achievement_id: string;
  unlocked_at: string;
};

type AchievementWithProgress = Achievement & {
  unlocked: boolean;
  unlocked_at?: string;
  current_value: number;
  progress_percentage: number;
};

const Achievements = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: achievements = [], isLoading: loading } = useQuery<AchievementWithProgress[]>({
    queryKey: ['achievements', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      // Fetch all achievements
      const { data: achievementsData, error: achievementsError } = await supabase
        .from("achievements")
        .select("*")
        .order("points_reward", { ascending: true });

      if (achievementsError) throw achievementsError;

      // Fetch user achievements
      const { data: userAchievementsData, error: userAchievementsError } = await supabase
        .from("user_achievements")
        .select("achievement_id, unlocked_at")
        .eq("user_id", user.id);

      if (userAchievementsError) throw userAchievementsError;

      // Fetch user gamification data for progress
      const { data: gamificationData, error: gamificationError } = await supabase
        .from("user_gamification")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (gamificationError && gamificationError.code !== "PGRST116") {
        throw gamificationError;
      }

      const userAchievements = new Map(
        (userAchievementsData || []).map((ua) => [ua.achievement_id, ua])
      );

      // Calculate progress for each achievement
      return (achievementsData || []).map(
        (achievement) => {
          const userAchievement = userAchievements.get(achievement.id);
          const unlocked = !!userAchievement;

          let current_value = 0;
          let progress_percentage = 0;

          if (gamificationData) {
            switch (achievement.requirement_type) {
              case "total_points":
                current_value = gamificationData.total_points;
                break;
              case "sessions_created":
                current_value = gamificationData.sessions_created;
                break;
              case "messages_sent":
                current_value = gamificationData.messages_sent;
                break;
              case "memories_created":
                current_value = gamificationData.memories_created;
                break;
              case "projects_created":
                current_value = gamificationData.projects_created;
                break;
              case "boards_created":
                current_value = gamificationData.boards_created;
                break;
              case "level":
                current_value = gamificationData.current_level;
                break;
            }

            progress_percentage = Math.min(
              100,
              (current_value / achievement.requirement_value) * 100
            );
          }

          return {
            ...achievement,
            unlocked,
            unlocked_at: userAchievement?.unlocked_at,
            current_value,
            progress_percentage,
          };
        }
      );
    },
    enabled: !!user,
  });

  const stats = useMemo(() => {
    const total = achievements.length;
    const unlocked = achievements.filter((a) => a.unlocked).length;
    const total_points = achievements.reduce((sum, a) => sum + a.points_reward, 0);
    const earned_points = achievements
      .filter((a) => a.unlocked)
      .reduce((sum, a) => sum + a.points_reward, 0);
    return { total, unlocked, total_points, earned_points };
  }, [achievements]);

  if (loading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-32 w-full" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Achievements</h1>
            <p className="text-muted-foreground">
              Track your progress and unlock rewards
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate("/profile")}>
            <User className="h-4 w-4 mr-2" />
            View Profile
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Achievements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Unlocked</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {stats.unlocked}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.total > 0 ? Math.round((stats.unlocked / stats.total) * 100) : 0}% complete
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Points Earned</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {stats.earned_points}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              of {stats.total_points} total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Completion</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Progress
                value={stats.total > 0 ? (stats.unlocked / stats.total) * 100 : 0}
                className="h-2"
              />
              <p className="text-xs text-muted-foreground">
                {stats.total - stats.unlocked} remaining
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {achievements.map((achievement) => (
          <Card
            key={achievement.id}
            className={
              achievement.unlocked
                ? "border-primary bg-primary/5"
                : "opacity-75"
            }
          >
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-4xl">{achievement.icon}</div>
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      {achievement.name}
                      {achievement.unlocked ? (
                        <Trophy className="h-4 w-4 text-primary" />
                      ) : (
                        <Lock className="h-4 w-4 text-muted-foreground" />
                      )}
                    </CardTitle>
                    <CardDescription className="text-sm mt-1">
                      {achievement.description}
                    </CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-medium">
                  {achievement.current_value} / {achievement.requirement_value}
                </span>
              </div>
              <Progress value={achievement.progress_percentage} className="h-2" />
              <div className="flex items-center justify-between">
                <Badge variant={achievement.unlocked ? "default" : "secondary"}>
                  {achievement.points_reward} points
                </Badge>
                {achievement.unlocked && achievement.unlocked_at && (
                  <span className="text-xs text-muted-foreground">
                    Unlocked {new Date(achievement.unlocked_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Achievements;
